/**
 * CSContractEditorPanel — Section 7 (iter-134).
 * Simple contract editor for Customer Service + Admin. Lists properties,
 * lets you toggle contracted_services on/off per property. Save writes
 * to both property.contracted_services and the latest signed contract's
 * services array, so the change is immediately reflected in the PM
 * services request grid and the auto-quote engine.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { FileText, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATALOG = [
  { key: 'bulk_removal', label: 'Bulk Removal' },
  { key: 'pressure_washing', label: 'Pressure Washing' },
  { key: 'window_cleaning', label: 'Window Cleaning' },
  { key: 'dumpster_treatment', label: 'Dumpster Treatment' },
  { key: 'trash_room_maintenance', label: 'Trash Room Maintenance' },
  { key: 'eco_friendly_amenities', label: 'Eco-Friendly Amenities' },
  { key: 'floor_care', label: 'Floor Care' },
  { key: 'floor_polishing', label: 'Floor Polishing' },
  { key: 'floor_restoration', label: 'Floor Restoration' },
  { key: 'day_porter', label: 'Day Porter' },
];

export default function CSContractEditorPanel({ testId = 'cs-contract-editor-panel' }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [services, setServices] = useState([]);

  useEffect(() => {
    (async () => {
      const r = await fetch(`${API}/api/customer-service/properties`, { credentials: 'include' });
      if (r.ok) setRows(await r.json());
    })();
  }, []);

  const openEditor = (p) => {
    setSelected(p);
    setServices(Array.isArray(p.contracted_services) && p.contracted_services.length
      ? p.contracted_services
      : ['bulk_removal', 'pressure_washing', 'window_cleaning', 'dumpster_treatment', 'eco_friendly_amenities']);
  };

  const toggle = (key) => {
    setServices((s) => s.includes(key) ? s.filter((x) => x !== key) : [...s, key]);
  };

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    const r = await fetch(`${API}/api/customer-service/properties/${selected.property_id}/contracted-services`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ services }),
    });
    setBusy(false);
    if (r.ok) {
      toast.success(`Contract updated · ${selected.property_name}`);
      setRows((rs) => rs.map((p) => p.property_id === selected.property_id ? { ...p, contracted_services: services } : p));
      setSelected(null);
    } else {
      const j = await r.json().catch(() => ({}));
      toast.error(j.detail || 'Save failed');
    }
  };

  const filtered = rows.filter((p) => !q || (p.property_name || '').toLowerCase().includes(q.toLowerCase()) || (p.manager_email || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 mt-4" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <FileText className="h-5 w-5 text-[#C9A962]" />
        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Contract Editor · Property Services
        </h3>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">Toggle services on/off per property. Changes reflect in the PM Services grid and auto-quote engine immediately.</p>

      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search property or PM email"
             className="bg-zinc-800 border-zinc-700 text-white mb-3 max-w-md" data-testid="cs-contract-search" />

      <table className="admin-stack-table text-sm" data-testid="cs-contract-table">
        <thead>
          <tr className="text-[11px] uppercase text-zinc-500 border-b border-zinc-800">
            <th className="text-left py-2 pr-3">Property</th>
            <th className="text-left py-2 pr-3">PM Email</th>
            <th className="text-left py-2 pr-3">Services</th>
            <th className="text-right py-2 pl-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((p) => (
            <tr key={p.property_id} className="border-b border-zinc-800/60 text-zinc-200" data-testid={`cs-property-row-${p.property_id}`}>
              <td className="py-3 pr-3" data-label="Property">{p.property_name}</td>
              <td className="py-3 pr-3 text-zinc-500" data-label="PM Email">{p.manager_email}</td>
              <td className="py-3 pr-3" data-label="Services">
                <div className="flex flex-wrap gap-1">
                  {(p.contracted_services || []).map((s) => (
                    <Badge key={s} className="bg-zinc-700 text-white text-[10px]">{s.replace(/_/g, ' ')}</Badge>
                  ))}
                  {!(p.contracted_services && p.contracted_services.length) && <span className="text-zinc-500 text-xs">— default —</span>}
                </div>
              </td>
              <td className="py-3 pl-3 text-right" data-label="Actions">
                <Button size="sm" variant="secondary" onClick={() => openEditor(p)} data-testid={`cs-contract-edit-${p.property_id}`}>Edit</Button>
              </td>
            </tr>
          ))}
          {!filtered.length && <tr><td colSpan={4} className="text-center text-zinc-500 py-4">No properties.</td></tr>}
        </tbody>
      </table>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
             onClick={() => setSelected(null)} data-testid="cs-contract-editor-modal">
          <div className="w-full max-w-lg rounded-2xl bg-zinc-900 border border-zinc-700 p-5" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-bold text-white mb-1">{selected.property_name}</h4>
            <p className="text-[11px] text-zinc-500 mb-4">Toggle services included in the contract.</p>
            <div className="grid grid-cols-2 gap-2">
              {CATALOG.map((c) => {
                const active = services.includes(c.key);
                return (
                  <button key={c.key} type="button" onClick={() => toggle(c.key)}
                    className={`px-3 py-2 rounded border-2 text-xs font-semibold transition ${active
                      ? 'bg-[#C9A962] border-[#C9A962] text-black'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-[#C9A962]/60'}`}
                    data-testid={`cs-contract-toggle-${c.key}`}
                    data-active={active}
                  >{c.label}</button>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="secondary" onClick={() => setSelected(null)}>Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold" data-testid="cs-contract-save">
                <Save className="h-4 w-4 mr-2" /> {busy ? 'Saving…' : 'Save contract'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
